import { type ApolloError, useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import LearnerObjectAttributesLearningGoalQuery from 'bundles/onboarding-common/queries/LearnerObjectAttributesLearningGoal.graphql';
import LearnerProfileLearningGoalQuery from 'bundles/onboarding-common/queries/LearnerProfileLearningGoal.graphql';
import type {
  LearnerObjectAttributesLearningGoalQuery as LearnerObjectAttributesLearningGoalQueryType,
  LearnerObjectAttributesLearningGoalQueryVariables,
} from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesLearningGoal';
import type {
  LearnerProfileLearningGoalQueryQuery,
  LearnerProfileLearningGoalQueryQueryVariables,
} from 'bundles/onboarding-common/queries/__generated__/LearnerProfileLearningGoal';
import { getShouldMigrateToLearnerObject } from 'bundles/onboarding-common/utils/experimentUtils';
import {
  formatLearningGoalFromLearnerObject,
  formatLearningGoalFromLearnerProfile,
} from 'bundles/onboarding-common/utils/savedQuestionDataUtils';

const useFetchLearningGoalData = () => {
  const shouldMigrateToLearnerObject = getShouldMigrateToLearnerObject();

  // LearnerObject query (new API)
  const [fetchLearnerObjectData] = useLazyQuery<
    LearnerObjectAttributesLearningGoalQueryType,
    LearnerObjectAttributesLearningGoalQueryVariables
  >(LearnerObjectAttributesLearningGoalQuery, {
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  // LearnerProfile query (legacy API)
  const [fetchLearnerProfileData] = useLazyQuery<
    LearnerProfileLearningGoalQueryQuery,
    LearnerProfileLearningGoalQueryQueryVariables
  >(LearnerProfileLearningGoalQuery, {
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  const handleError = (error: ApolloError | Error) => {
    Sentry.captureMessage('Error fetching learning goal data', {
      extra: {
        error: error.message,
      },
    });
  };

  const fetchLearningGoalData = async () => {
    try {
      // Use LearnerObject API when epic is enabled, otherwise use LearnerProfile API
      if (shouldMigrateToLearnerObject) {
        const { data, error } = await fetchLearnerObjectData();

        if (error) {
          handleError(error);
          return null;
        }

        return formatLearningGoalFromLearnerObject(data);
      } else {
        const { data, error } = await fetchLearnerProfileData();

        if (error) {
          handleError(error);
          return null;
        }

        return formatLearningGoalFromLearnerProfile(data);
      }
    } catch (error) {
      handleError(error);

      return null;
    }
  };

  return {
    fetchLearningGoalData,
  };
};

export default useFetchLearningGoalData;
